import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { listStatusPages, listMonitors, updateStatusPage, StatusPageParams } from "../lib/api";
import { StatusPage, Monitor } from "../types";
import { Bone } from "../components/Skeleton";

interface FormValues {
  title: string;
  slug: string;
  description: string;
}

interface MonitorSelection {
  included: boolean;
  displayName: string;
}

export default function StatusPageEditor() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const { data: pagesData, isLoading: pagesLoading } = useQuery({ queryKey: ["status-pages"], queryFn: listStatusPages });
  const { data: monitorsData } = useQuery({ queryKey: ["monitors"], queryFn: listMonitors });

  const page: StatusPage | undefined = pagesData?.pages?.find((p: StatusPage) => p.id === id);
  const monitors: Monitor[] = monitorsData?.monitors ?? [];

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<FormValues>();

  // Per-monitor selection state: map from monitor_id → { included, displayName }
  const [selections, setSelections] = useState<Record<string, MonitorSelection>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Populate form and selections once the page data loads.
  useEffect(() => {
    if (!page) return;
    reset({
      title: page.title,
      slug: page.slug,
      description: page.description ?? "",
    });
    const sel: Record<string, MonitorSelection> = {};
    for (const m of page.monitors) {
      sel[m.monitor_id] = { included: true, displayName: m.display_name ?? "" };
    }
    setSelections(sel);
  }, [page, reset]);

  const mut = useMutation({
    mutationFn: (params: StatusPageParams) => updateStatusPage(id!, params),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["status-pages"] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (e) => setApiError(e instanceof Error ? e.message : "Failed to save"),
  });

  function onSubmit(values: FormValues) {
    setApiError(null);
    const monitorInputs = monitors
      .filter((m) => selections[m.id]?.included)
      .map((m, i) => ({
        monitor_id: m.id,
        display_name: selections[m.id]?.displayName || null,
        display_order: i,
      }));

    mut.mutate({
      title: values.title,
      slug: values.slug,
      description: values.description || null,
      is_public: true,
      monitors: monitorInputs,
    });
  }

  function toggleMonitor(id: string) {
    setSelections((prev) => ({
      ...prev,
      [id]: { included: !prev[id]?.included, displayName: prev[id]?.displayName ?? "" },
    }));
  }

  function setDisplayName(id: string, name: string) {
    setSelections((prev) => ({
      ...prev,
      [id]: { included: prev[id]?.included ?? false, displayName: name },
    }));
  }

  function toSlug(v: string) {
    return v.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
  }

  if (pagesLoading || !page) {
    return (
      <div style={{ minHeight: "100vh" }}>
        <div style={{ maxWidth: 700, margin: "0 auto", padding: "40px 24px" }}>
          <div style={{ marginBottom: 24 }}>
            <Bone w={100} h={13} style={{ marginBottom: 14 }} />
            <Bone w={180} h={24} style={{ marginBottom: 8 }} />
            <Bone w={220} h={13} />
          </div>
          <div className="glass" style={{ padding: 24, marginBottom: 20, display: "flex", flexDirection: "column", gap: 18 }}>
            <Bone w={100} h={15} />
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <Bone w={48} h={11} />
              <Bone w="100%" h={40} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <Bone w={40} h={11} />
              <Bone w="100%" h={40} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <Bone w={100} h={11} />
              <Bone w="100%" h={60} />
            </div>
          </div>
          <div className="glass" style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
            <Bone w={88} h={15} />
            <Bone w={320} h={12} />
            {[...Array(3)].map((_, i) => (
              <div key={i} style={{ padding: "12px 14px", borderRadius: 3, border: "1px solid rgba(255,255,255,0.07)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Bone w={15} h={15} />
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    <Bone w={120} h={14} />
                    <Bone w={200} h={11} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh" }}>
      <div style={{ maxWidth: 700, margin: "0 auto", padding: "40px 24px" }}>
        <div style={{ marginBottom: 24 }}>
          <Link to="/dashboard/status-pages" style={{ fontSize: 13, color: "#98A1AB", textDecoration: "none" }}>
            ← Status Pages
          </Link>
          <h1 style={{ margin: "12px 0 4px", fontSize: 24, fontWeight: 700, color: "#EFF2F5" }}>Manage Page</h1>
          <a
            href={`/status/${page.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 13, color: "#FF2566", textDecoration: "none" }}
          >
            worldrtt.com/status/{page.slug} ↗
          </a>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {apiError && <p style={{ color: "#FF5C5C", fontSize: 13, margin: 0 }}>{apiError}</p>}

          <div className="glass" style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "#EFF2F5" }}>Page Details</h3>

            <div>
              <label style={labelStyle}>Title</label>
              <input
                {...register("title", { required: "Title is required" })}
                className="input-dark"
                onChange={(e) => {
                  setValue("title", e.target.value);
                  setValue("slug", toSlug(e.target.value));
                }}
              />
              {errors.title && <p style={errorStyle}>{errors.title.message}</p>}
            </div>

            <div>
              <label style={labelStyle}>Slug</label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 13, color: "#98A1AB", whiteSpace: "nowrap" }}>worldrtt.com/status/</span>
                <input
                  {...register("slug", {
                    required: "Slug is required",
                    pattern: { value: /^[a-z0-9][a-z0-9-]{1,58}[a-z0-9]$/, message: "3–60 chars, lowercase, hyphens" },
                  })}
                  className="input-dark"
                />
              </div>
              {errors.slug && <p style={errorStyle}>{errors.slug.message}</p>}
            </div>

            <div>
              <label style={labelStyle}>Description (optional)</label>
              <textarea
                {...register("description")}
                className="input-dark"
                rows={2}
                placeholder="Real-time status of our services."
                style={{ resize: "vertical" }}
              />
            </div>
          </div>

          <div className="glass" style={{ padding: 24 }}>
            <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 600, color: "#EFF2F5" }}>Monitors</h3>
            <p style={{ margin: "0 0 16px", fontSize: 12, color: "#98A1AB" }}>
              Select which monitors appear on the page. Set an optional display name to override the monitor label.
            </p>

            {monitors.length === 0 ? (
              <p style={{ color: "#98A1AB", fontSize: 13 }}>
                No monitors yet. <Link to="/dashboard/monitors" style={{ color: "#FF2566", textDecoration: "none" }}>Add a monitor</Link> first.
              </p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {monitors.map((m) => {
                  const sel = selections[m.id];
                  const included = sel?.included ?? false;
                  return (
                    <div
                      key={m.id}
                      style={{
                        padding: "12px 14px",
                        borderRadius: 3,
                        border: `1px solid ${included ? "rgba(57,217,138,0.25)" : "rgba(255,255,255,0.07)"}`,
                        background: included ? "rgba(57,217,138,0.04)" : "transparent",
                        transition: "border-color 0.15s, background 0.15s",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={included}
                          onChange={() => toggleMonitor(m.id)}
                          style={{ accentColor: "#39D98A", width: 15, height: 15, cursor: "pointer" }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: "#EFF2F5" }}>{m.name}</div>
                          <div style={{ fontSize: 11, color: "#98A1AB", marginTop: 1 }}>{m.url} · every {m.interval_minutes}m</div>
                        </div>
                      </div>
                      {included && (
                        <div style={{ marginTop: 10, paddingLeft: 25 }}>
                          <label style={{ ...labelStyle, marginBottom: 4 }}>Display name (optional)</label>
                          <input
                            value={sel?.displayName ?? ""}
                            onChange={(e) => setDisplayName(m.id, e.target.value)}
                            className="input-dark"
                            placeholder={m.name}
                            style={{ fontSize: 13 }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button type="submit" disabled={mut.isPending} className="btn-primary" style={{ padding: "11px 28px", fontSize: 14 }}>
              {mut.isPending ? "Saving…" : "Save Changes"}
            </button>
            {saved && <span style={{ fontSize: 13, color: "#39D98A" }}>Saved</span>}
          </div>
        </form>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: "block", fontSize: 12, fontWeight: 600, color: "#98A1AB",
  marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em",
};
const errorStyle: React.CSSProperties = { margin: "4px 0 0", fontSize: 12, color: "#FF5C5C" };
